import { useMutation, useQuery } from "@tanstack/react-query";
import { FileSignature } from "lucide-react";

import type { BillingSignedDocument } from "../../shared/billing-document-schemas";
import { COMMERCIAL_DOCUMENT_LABELS } from "../../shared/commercial-documents";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { addSignedDocumentToBilling, getSignedDocuments, signedDocumentsKey } from "./billing-api";

/**
 * Signed documents not in billing yet (ADR-0073). A signed order form goes to billing on its own
 * when it is filed; what is listed here was filed earlier, or needs a person: a change order, or
 * an order form whose terms do not convert cleanly. An order form is added in one click; anything
 * that needs review opens the terms form filled in from the document.
 */

function Waiting({
  customerSlug,
  document,
  canManage,
  onReview,
  onAdded,
}: {
  customerSlug: string;
  document: BillingSignedDocument;
  canManage: boolean;
  onReview: (document: BillingSignedDocument) => void;
  onAdded: () => Promise<void>;
}) {
  const add = useMutation({
    mutationFn: () => addSignedDocumentToBilling(customerSlug, document.documentId),
    onSuccess: onAdded,
  });
  const orderForm = document.kind === "order_form";
  return (
    <li className="flex flex-col gap-3 rounded-xl bg-info-background px-6 py-4 text-info-foreground">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <FileSignature className="mt-0.5 size-5 shrink-0" aria-hidden />
          <p className="m-0">
            <span className="font-semibold">
              {COMMERCIAL_DOCUMENT_LABELS[document.kind]} {document.reference}
            </span>{" "}
            was signed on {document.signedOn} and isn&rsquo;t in billing yet.
          </p>
        </div>
        {canManage ? (
          <Button
            type="button"
            disabled={add.isPending}
            onClick={() => {
              if (orderForm && !add.error) add.mutate();
              else onReview(document);
            }}
          >
            {orderForm && !add.error ? "Add to billing" : "Review and add"}
          </Button>
        ) : null}
      </div>
      {add.error ? <FieldError>{add.error.message}</FieldError> : null}
    </li>
  );
}

export function BillingSignedDocuments({
  customerSlug,
  canManage,
  onReview,
  onAdded,
}: {
  customerSlug: string;
  canManage: boolean;
  onReview: (document: BillingSignedDocument) => void;
  onAdded: () => Promise<void>;
}) {
  const signed = useQuery({
    queryKey: signedDocumentsKey(customerSlug),
    queryFn: () => getSignedDocuments(customerSlug),
  });
  const waiting = (signed.data?.documents ?? []).filter((document) => document.termsId === null);
  if (waiting.length === 0) return null;
  return (
    <ul className="flex flex-col gap-3" aria-label="Signed, not in billing yet">
      {waiting.map((document) => (
        <Waiting
          key={document.documentId}
          customerSlug={customerSlug}
          document={document}
          canManage={canManage}
          onReview={onReview}
          onAdded={onAdded}
        />
      ))}
    </ul>
  );
}
